import React from "react";

import { ReactNode, RefObject } from "react";
import { ErrorHandler } from "src/abstracts/handleError";
import { Skeleton } from "src/shared-components/skeleton";
import { GenericNullable } from "src/types/GenericNullable";
import { TermsOfServiceScreenFooter } from "./sections/footer";
import { HeaderComponent } from "src/shared-components/header";
import { FooterComponent } from "src/shared-components/footer";
import { TermsOfServiceScreenHeader } from "./sections/header";
import { TermsOfServiceScreenContent } from "./sections/content";
import { DynamicSection } from "src/composable/base-dynamic-section";

import {
  TermsOfService,
  TermsOfServicesRepository,
} from "src/components/terms-of-service";

interface TermsOfServiceScreenState extends GenericNullable<TermsOfService> {
  isLoading: boolean;
  selectedVersion: string | null;
}

export default class TermsOfServiceScreen extends DynamicSection<
  {},
  TermsOfServiceScreenState
> {
  mainDivRef: RefObject<HTMLDivElement>;
  _termsOfServicesRepository = new TermsOfServicesRepository();

  state: Readonly<TermsOfServiceScreenState> = {
    content: null,
    version: null,
    availableVersions: null,
    asOfDate: null,
    selectedVersion: null,
    isLoading: true,
  };

  constructor(props: {}) {
    super(props);
    this.mainDivRef = React.createRef();
  }

  componentDidMount = async () => {
    // smooth scroll to top when screen is loaded
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      this.setState({ isLoading: true });
      await this.getItemsFromServerAndSaveToState();
    } catch (e) {
      ErrorHandler.displayError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  setToAllStates = (termsOfService: TermsOfService) => {
    this.setState({
      content: termsOfService.content,
      version: termsOfService.version,
      availableVersions: termsOfService.availableVersions,
      asOfDate: termsOfService.asOfDate,
    });
  };

  getItemsFromServerAndSaveToState = async () => {
    try {
      this.setState({ isLoading: true });
      const termsOfService = await this._termsOfServicesRepository.getLatest();
      this.setToAllStates(termsOfService);
      this.setState({
        selectedVersion: termsOfService.version,
      });
    } catch (e) {
      ErrorHandler.displayError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getItemsWithSpecifiedVersion = async (version: string) => {
    try {
      this.setState({ isLoading: true });
      const termsOfService = await this._termsOfServicesRepository.getByVersion(
        version
      );
      this.setToAllStates(termsOfService);
      this.setState({
        selectedVersion: version,
      });
    } catch (e) {
      ErrorHandler.displayError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  onSelectedVersionInputChanged = (version: string | null) => {
    this.setState({ selectedVersion: version });
    version && this.getItemsWithSpecifiedVersion(version);
    // scroll to the top of the main div
    if (this.mainDivRef.current) {
      this.mainDivRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  render(): ReactNode {
    const { content, selectedVersion, availableVersions, asOfDate, isLoading } =
      this.state;

    return (
      <>
        {/* header */}
        <HeaderComponent />

        {/* content */}
        <main
          ref={this.mainDivRef}
          className="flex flex-col py-8 px-6 tablet:px-16 tablet:py-12 desktop:px-24 desktop:py-16 gap-16"
        >
          {isLoading ? (
            <div className="flex flex-col gap-8">
              <Skeleton className="h-20" numberOfSkeletons={15} />
            </div>
          ) : (
            <>
              <TermsOfServiceScreenHeader asOfDate={asOfDate} />
              <TermsOfServiceScreenContent content={content} />
              <TermsOfServiceScreenFooter
                versionSelectorConfig={{
                  selectedValue: selectedVersion,
                  onSelectedInputChange: this.onSelectedVersionInputChanged,
                  values: availableVersions,
                }}
              />
            </>
          )}
        </main>

        {/* footer */}
        <FooterComponent />
      </>
    );
  }
}
